import { NETWORK_COLORS } from '@/constants/colors'
import type { BreakdownItem } from '@/types/analytics'
import type { Vault } from '@/types/vault'
import { UNKNOWN_ASSET_GROUP, getAssetGroupLabel, getAssetGroupToken } from '@/utils/analytics/tvlGroups'
import { getCuratorDisplayName } from '@/utils/curator'
import { getNetworkName } from '@/utils/network/getNetworkName'
import { getProtocolDisplayName } from '@/utils/protocol'
import { getCuratorImage, getNetworkImage, getProtocolImage, getTokenImage } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'

const NETWORK_THRESHOLD_USD = 1_000_000_000
const PROTOCOL_THRESHOLD_USD = 2_000_000_000
const CURATOR_THRESHOLD_USD = 100_000_000

type RawBreakdownItem = {
  name: string
  displayName: string
  tvl: number
  imageUrl: string | null
  imageFallbackUrl: string | null
  color?: string
}

type DisplayTvl = {
  displayName: string
  tvl: number
}

function computeBreakdown(items: RawBreakdownItem[], total: number): BreakdownItem[] {
  return [...items]
    .sort((a, b) => b.tvl - a.tvl)
    .map((item) => ({
      name: item.name,
      displayName: item.displayName,
      value: item.tvl,
      percentage: total > 0 ? item.tvl / total : 0,
      imageUrl: item.imageUrl,
      imageFallbackUrl: item.imageFallbackUrl,
      color: item.color,
    }))
}

function groupItems(items: BreakdownItem[], shouldGroup: (item: BreakdownItem) => boolean): BreakdownItem[] {
  const main: BreakdownItem[] = []
  const small: BreakdownItem[] = []

  for (const item of items) {
    if (shouldGroup(item)) small.push(item)
    else main.push(item)
  }

  if (small.length === 0) return main

  let otherValue = 0
  let otherPercentage = 0
  for (const item of small) {
    otherValue += item.value
    otherPercentage += item.percentage
  }

  return [
    ...main,
    {
      name: 'other',
      displayName: 'Other',
      value: otherValue,
      percentage: otherPercentage,
      imageUrl: null,
      imageFallbackUrl: null,
      subItems: small,
    },
  ]
}

export function useTvlBreakdowns(vaults: Vault[] | null | undefined) {
  return useMemo(() => {
    if (!vaults) return null

    const networkMap = new Map<string, number>()
    const assetMap = new Map<string, number>()
    const protocolMap = new Map<string, DisplayTvl>()
    const curatorMap = new Map<string, DisplayTvl>()
    let total = 0
    let lastUpdateTimestamp: number | null = null
    const uniqueCurators = new Set<string>()

    for (const vault of vaults) {
      const tvl = vault.tvl?.usd ? Number.parseFloat(vault.tvl.usd) : 0
      total += tvl

      if (vault.lastUpdateTimestamp != null) {
        lastUpdateTimestamp =
          lastUpdateTimestamp === null
            ? vault.lastUpdateTimestamp
            : Math.max(lastUpdateTimestamp, vault.lastUpdateTimestamp)
      }

      networkMap.set(vault.network.name, (networkMap.get(vault.network.name) ?? 0) + tvl)

      const assetGroup = vault.asset.assetGroup ?? UNKNOWN_ASSET_GROUP
      assetMap.set(assetGroup, (assetMap.get(assetGroup) ?? 0) + tvl)

      const protocolTvl = protocolMap.get(vault.protocol.name)?.tvl ?? 0
      protocolMap.set(vault.protocol.name, {
        displayName: getProtocolDisplayName(vault.protocol),
        tvl: protocolTvl + tvl,
      })

      if (vault.curator?.name) {
        uniqueCurators.add(vault.curator.name)
        const curatorTvl = curatorMap.get(vault.curator.name)?.tvl ?? 0
        curatorMap.set(vault.curator.name, {
          displayName: getCuratorDisplayName(vault.curator),
          tvl: curatorTvl + tvl,
        })
      }
    }

    return {
      total,
      lastUpdateTimestamp,
      vaultCount: vaults.length,
      protocolCount: protocolMap.size,
      curatorCount: uniqueCurators.size,
      network: groupItems(
        computeBreakdown(
          Array.from(networkMap.entries(), ([name, tvl]) => ({
            name,
            displayName: getNetworkName(name),
            imageUrl: getNetworkImage(name),
            imageFallbackUrl: getNetworkImage('fallback'),
            color: NETWORK_COLORS[name as keyof typeof NETWORK_COLORS],
            tvl,
          })),
          total
        ),
        (item) => item.value < NETWORK_THRESHOLD_USD
      ),
      asset: computeBreakdown(
        Array.from(assetMap.entries(), ([key, tvl]) => ({
          name: key,
          displayName: getAssetGroupLabel(key),
          imageUrl: getTokenImage(getAssetGroupToken(key)),
          imageFallbackUrl: getTokenImage('fallback'),
          tvl,
        })),
        total
      ),
      protocol: groupItems(
        computeBreakdown(
          Array.from(protocolMap.entries(), ([name, protocol]) => ({
            name,
            displayName: protocol.displayName,
            imageUrl: getProtocolImage(name),
            imageFallbackUrl: getProtocolImage('fallback'),
            tvl: protocol.tvl,
          })),
          total
        ),
        (item) => item.value < PROTOCOL_THRESHOLD_USD
      ),
      curator: groupItems(
        computeBreakdown(
          Array.from(curatorMap.entries(), ([name, curator]) => ({
            name,
            displayName: curator.displayName,
            imageUrl: getCuratorImage(name),
            imageFallbackUrl: getCuratorImage('fallback'),
            tvl: curator.tvl,
          })),
          total
        ),
        (item) => item.value < CURATOR_THRESHOLD_USD
      ),
    }
  }, [vaults])
}
